import { Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ColumnFiltersState,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { Teacher } from '../../types';

interface TeacherTableProps {
  teachers: Teacher[];
  onEdit: (teacher: Teacher) => void;
  onDelete: (teacher: Teacher) => void;
  onRowClick?: (teacher: Teacher) => void;
}

const columnHelper = createColumnHelper<Teacher>();

export const TeacherTable: React.FC<TeacherTableProps> = ({ teachers, onEdit, onDelete, onRowClick }) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        id: 'name',
        header: 'Name',
        cell: (info) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {info.getValue()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {info.row.original.email}
            </Typography>
          </Box>
        ),
      }),
      columnHelper.accessor('subjects', {
        header: 'Subjects',
        cell: (info) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {info
              .getValue()
              .slice(0, 2)
              .map((subject) => (
                <Chip key={subject} label={subject} size="small" variant="outlined" />
              ))}
            {info.getValue().length > 2 && (
              <Chip label={`+${info.getValue().length - 2}`} size="small" variant="outlined" />
            )}
          </Box>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('roles', {
        header: 'Roles',
        cell: (info) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {info.getValue().map((role) => (
              <Chip
                key={role}
                label={role}
                size="small"
                color={
                  role === 'SLT' || role === 'HeadTeacher'
                    ? 'primary'
                    : role === 'HoD'
                      ? 'secondary'
                      : role === 'HoY'
                        ? 'info'
                        : 'default'
                }
              />
            ))}
          </Box>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('contractType', {
        header: 'Contract',
        cell: (info) => (
          <Chip
            label={info.getValue()}
            size="small"
            color={
              info.getValue() === 'full-time' ? 'success' : info.getValue() === 'part-time' ? 'warning' : 'default'
            }
          />
        ),
      }),
      columnHelper.accessor('maxPeriodsPerWeek', {
        header: 'Max Periods',
        cell: (info) => <Typography variant="body2">{info.getValue()} periods/week</Typography>,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(info.row.original);
              }}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(info.row.original);
              }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      }),
    ],
    [onEdit, onDelete],
  );

  const table = useReactTable({
    data: teachers,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search teachers..."
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    sx={{
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted() as string] ?? null}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: onRowClick ? 'action.hover' : 'inherit',
                  },
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {table.getRowModel().rows.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No teachers found
          </Typography>
        </Box>
      )}
    </Box>
  );
};
